import React, { FC, useEffect, useState } from 'react';
import { Button, Col, Drawer, Modal, Row, Tooltip } from 'antd';
import { useMediaPredicate } from 'react-media-hook';
import { Close, WarningCircle } from 'common/svg';
import { Selector } from 'common/components/Selector/Selector';
import { Loader } from 'common/components/Loader';
import { DataTable } from 'common/components/Table/DataTable';
import { MEDIA_MOBILE_768 } from 'common/const/common.const';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getPurchaseOrderRecoBasisModel } from 'app/store/reducers/purchaseOrder.reducer';
import {
  IPurchaseOrderStoreModel,
  IRecoBasisProductSetDataSourceItem,
  IRecoBasisSafetyStockDataSourceItem,
  IRecoBasisSellingPeriodDataSourceItem,
  IRecoBasisTrendDataSourceItem,
  IRecoBasisUnitsSoldDataSourceItem,
  recoBasisProductSetConfig,
  recoBasisSafetyStockConfig,
  recoBasisSellingPeriodConfig,
  recoBasisTrendConfig,
  recoBasisUnitsSoldDataConfig,
} from 'entities/PurchaseOrder/PurchaseOrder.models';
import { buildRecoBasisDataSources } from 'entities/PurchaseOrder/PurchaseOrder.helper';

interface IComponentProps {
  open: boolean;
  store?: IPurchaseOrderStoreModel;
  codes?: string[];
  onCancel: () => void;
}

export const PurchaseOrderRecoBasis: FC<IComponentProps> = ({ open, store, codes = [], onCancel }) => {
  const dispatch = useAppDispatch();
  const { purchaseOrderRecoBasisModel, purchaseOrderRecoBasisModelLoading } = useAppSelector(
    (state) => state.purchaseOrder
  );
  const [code, setCode] = useState<string>('');
  const isMobile: boolean = useMediaPredicate(MEDIA_MOBILE_768);

  useEffect(() => {
    if (open && codes.length) {
      setCode(codes[0]);
    }
  }, [open, codes.length]);

  const codesOptions = codes.map((item) => ({ label: item, value: item }));
  const {
    unitsSoldDataSource,
    trendDataSource,
    adjDemandDataSource,
    sellingPeriodDataSource,
    productSetDataSource,
    safetyStockDataSource,
  } = buildRecoBasisDataSources(purchaseOrderRecoBasisModel);

  const changeCode = (code?: string | number): void => {
    if (!store || !code) {
      return;
    }

    setCode(code as string);
    dispatch(
      getPurchaseOrderRecoBasisModel({
        storeCode: store.code,
        location: store.location,
        prodCode: code as string,
      })
    );
  };

  const renderSteps = () => {
    return (
      <>
        <Row className="purchase-order-reco-basis-modal-row">
          <Col span={24} className="mb-2 flex flex-align-center">
            <div className="mr-3 purchase-order-reco-basis-modal-step">Step 1</div>
            <span className="ff-medium fs-xxs">12 week historical baseline demand determined</span>
          </Col>

          <Col span={24}>
            <DataTable<IRecoBasisUnitsSoldDataSourceItem>
              dataSource={unitsSoldDataSource}
              config={recoBasisUnitsSoldDataConfig}
            />
          </Col>
        </Row>

        <Row className="purchase-order-reco-basis-modal-row">
          <Col span={24} className="mb-2 flex flex-align-center">
            <div className="mr-3 purchase-order-reco-basis-modal-step">Step 2</div>
            <span className="ff-medium fs-xxs">Overall trendline of sales computed</span>
          </Col>

          <Col span={24}>
            <DataTable<IRecoBasisTrendDataSourceItem> dataSource={trendDataSource} config={recoBasisTrendConfig} />
          </Col>
        </Row>

        <Row className="purchase-order-reco-basis-modal-row">
          <Col span={24} className="mb-2 flex flex-align-center">
            <div className="mr-3 purchase-order-reco-basis-modal-step">Step 3</div>
            <span className="ff-medium fs-xxs">Baseline demand with trendline of sales adjusted</span>
          </Col>

          <Col span={24}>
            <DataTable<IRecoBasisUnitsSoldDataSourceItem>
              dataSource={adjDemandDataSource}
              config={recoBasisUnitsSoldDataConfig}
            />
          </Col>
        </Row>

        <Row className="purchase-order-reco-basis-modal-row">
          <Col span={24} className="mb-2 flex flex-align-center">
            <div className="mr-3 purchase-order-reco-basis-modal-step">Step 4</div>
            <span className="ff-medium fs-xxs">Current inventory considered</span>
          </Col>

          <Col span={24}>
            <DataTable<IRecoBasisSellingPeriodDataSourceItem>
              dataSource={sellingPeriodDataSource}
              config={recoBasisSellingPeriodConfig}
            />
          </Col>
        </Row>

        <Row className="purchase-order-reco-basis-modal-row">
          <Col span={24} className="mb-2 flex flex-align-center">
            <div className="mr-3 purchase-order-reco-basis-modal-step">Step 5</div>
            <span className="ff-medium fs-xxs">Product set sizes considered</span>
          </Col>

          <Col span={24}>
            <DataTable<IRecoBasisProductSetDataSourceItem>
              dataSource={productSetDataSource}
              config={recoBasisProductSetConfig}
            />
          </Col>
        </Row>

        <Row className="purchase-order-reco-basis-modal-row">
          <Col span={24} className="mb-2 flex flex-align-center">
            <div className="mr-3 purchase-order-reco-basis-modal-step">Step 6</div>
            <span className="ff-medium fs-xxs">Safety stock added</span>
          </Col>

          <Col span={24}>
            <DataTable<IRecoBasisSafetyStockDataSourceItem>
              dataSource={safetyStockDataSource}
              config={recoBasisSafetyStockConfig}
            />
          </Col>
        </Row>
      </>
    );
  };

  if (isMobile) {
    return (
      <Drawer
        open={open}
        rootClassName="purchase-order-reco-basis-card"
        placement="bottom"
        height="100vh"
        destroyOnClose
        classNames={{ body: 'scrollbar-x' }}
        title={
          <div className="flex flex-align-center">
            <span>Reco QTY Basis</span>
            <Tooltip
              title="Reco generated based on your store's sales history from previous weeks with buffer"
              placement="bottom"
              overlayInnerStyle={{ color: '#8491ae' }}
            >
              <WarningCircle className="ml-2 icon-gray" />
            </Tooltip>
          </div>
        }
        closeIcon={null}
        extra={
          <div className="flex flex-align-center">
            <Selector options={codesOptions} allowClear={false} value={code} onChange={changeCode} />
            <div className="ml-3">
              <Button className="btn-gray h-44 square" icon={<Close className="icon-gray" />} onClick={onCancel} />
            </div>
          </div>
        }
      >
        <div className="purchase-order-reco-basis-card-scroll" style={{ width: 'max-content' }}>
          {renderSteps()}
        </div>
      </Drawer>
    );
  }

  return (
    <Modal
      open={open}
      rootClassName="modal purchase-order-reco-basis-modal"
      width={720}
      height={600}
      centered
      destroyOnClose
      title={
        <div className="flex flex-align-center">
          <span>Recommended Quantity Basis</span>
          <Tooltip
            title="Reco generated based on your store's sales history from previous weeks with buffer"
            placement="bottom"
            overlayInnerStyle={{ color: '#8491ae' }}
          >
            <WarningCircle className="ml-2 icon-gray" />
          </Tooltip>
        </div>
      }
      closeIcon={<Close className="icon-gray" />}
      classNames={{ body: 'p-0 scrollbar-y' }}
      onCancel={onCancel}
      footer={null}
    >
      <>
        {purchaseOrderRecoBasisModelLoading && <Loader />}
        <Row className="purchase-order-reco-basis-modal-row">
          <Col>
            <Selector options={codesOptions} allowClear={false} value={code} onChange={changeCode} />
          </Col>
        </Row>

        {renderSteps()}
      </>
    </Modal>
  );
};
